"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";
import { NodeBasicInfo } from "@/contexts/NodeListContext";
import {
    calculateMonthlyCostCNY,
    calculateYearlyCostCNY,
    formatCNY,
    getCurrencyCode,
} from "@/utils/currencyHelper";

interface CostDetailsDialogProps {
    nodeList: NodeBasicInfo[];
    exchangeRates: Record<string, number>;
    type: "monthly" | "yearly";
}

// Get billing cycle description
function getBillingCycleText(billingCycle: number, t: (key: string) => string): string {
    if (billingCycle <= 0 || billingCycle === -1) {
        return t("common.once");
    }
    if (billingCycle >= 28 && billingCycle <= 31) {
        return t("common.monthly");
    }
    if (billingCycle >= 89 && billingCycle <= 92) {
        return t("common.quarterly");
    }
    if (billingCycle >= 180 && billingCycle <= 186) {
        return t("common.semi_annual");
    }
    if (billingCycle >= 360 && billingCycle <= 370) {
        return t("common.annual");
    }
    if (billingCycle >= 720 && billingCycle <= 740) {
        return t("common.biennial");
    }
    if (billingCycle >= 1080 && billingCycle <= 1100) {
        return t("common.triennial");
    }
    return `${billingCycle} ${t("common.day")}`;
}

export function CostDetailsDialog({
    nodeList,
    exchangeRates,
    type,
}: CostDetailsDialogProps) {
    const { t } = useTranslation();

    // Filter nodes that have a price (price > 0 or price === -1 for free)
    const nodesWithPrice = nodeList.filter(
        (node) => node.price !== 0 && node.billing_cycle !== 0
    );

    // Calculate costs for each node
    const nodesCostDetails = nodesWithPrice.map((node) => {
        const monthlyCost = calculateMonthlyCostCNY(
            node.price,
            node.billing_cycle,
            node.currency,
            exchangeRates
        );
        const yearlyCost = calculateYearlyCostCNY(
            node.price,
            node.billing_cycle,
            node.currency,
            exchangeRates
        );

        return {
            name: node.name,
            originalPrice: node.price,
            currency: node.currency,
            currencyCode: getCurrencyCode(node.currency),
            billingCycle: node.billing_cycle,
            billingCycleText: getBillingCycleText(node.billing_cycle, t),
            monthlyCost,
            yearlyCost,
            isFree: node.price === -1,
        };
    });

    // Sort by cost (highest first)
    const sortedNodes = nodesCostDetails.sort((a, b) => {
        if (type === "monthly") {
            return b.monthlyCost - a.monthlyCost;
        }
        return b.yearlyCost - a.yearlyCost;
    });

    // Calculate total
    const totalMonthlyCost = sortedNodes.reduce((acc, node) => acc + node.monthlyCost, 0);
    const totalYearlyCost = sortedNodes.reduce((acc, node) => acc + node.yearlyCost, 0);

    const dialogTitle = type === "monthly"
        ? t("cost_details.monthly_title")
        : t("cost_details.yearly_title");

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 ml-1 opacity-60 hover:opacity-100"
                >
                    <Info className="h-3 w-3" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{dialogTitle}</DialogTitle>
                </DialogHeader>
                <div className="mt-4">
                    {sortedNodes.length === 0 ? (
                        <div className="text-center text-muted-foreground py-8">
                            {t("cost_details.no_data")}
                        </div>
                    ) : (
                        <>
                            {/* Table Header */}
                            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/50 rounded-t-md text-sm font-medium text-muted-foreground">
                                <div className="col-span-4">{t("cost_details.server_name")}</div>
                                <div className="col-span-3 text-right">{t("cost_details.original_price")}</div>
                                <div className="col-span-2 text-center">{t("cost_details.billing_cycle")}</div>
                                <div className="col-span-3 text-right">
                                    {type === "monthly"
                                        ? t("cost_details.monthly_cost_cny")
                                        : t("cost_details.yearly_cost_cny")}
                                </div>
                            </div>

                            {/* Table Body */}
                            <div className="divide-y divide-border">
                                {sortedNodes.map((node, index) => (
                                    <div
                                        key={index}
                                        className="grid grid-cols-12 gap-2 px-3 py-2.5 text-sm hover:bg-muted/30"
                                    >
                                        <div className="col-span-4 truncate font-medium" title={node.name}>
                                            {node.name}
                                        </div>
                                        <div className="col-span-3 text-right text-muted-foreground">
                                            {node.isFree ? (
                                                <span className="text-green-600">{t("common.free")}</span>
                                            ) : (
                                                `${node.currency}${node.originalPrice}`
                                            )}
                                        </div>
                                        <div className="col-span-2 text-center text-muted-foreground">
                                            {node.billingCycleText}
                                        </div>
                                        <div className="col-span-3 text-right font-medium">
                                            {node.isFree ? (
                                                <span className="text-green-600">¥0.00</span>
                                            ) : (
                                                formatCNY(type === "monthly" ? node.monthlyCost : node.yearlyCost)
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Total Row */}
                            <div className="grid grid-cols-12 gap-2 px-3 py-3 bg-muted/50 rounded-b-md text-sm font-bold border-t-2 border-primary/20">
                                <div className="col-span-4">{t("cost_details.total")}</div>
                                <div className="col-span-5"></div>
                                <div className="col-span-3 text-right text-primary">
                                    {formatCNY(type === "monthly" ? totalMonthlyCost : totalYearlyCost)}
                                </div>
                            </div>

                            {/* Note */}
                            <div className="mt-4 text-xs text-muted-foreground">
                                <p>{t("cost_details.note")}</p>
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
